import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowPathIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useProjects } from '../context/ProjectsContext';
import { devCapacityService } from '../services/devCapacityService';

export default function CapacityOverview() {
  const navigate = useNavigate();
  const { projects, loading, fetchProjects } = useProjects();
  const [devCounts, setDevCounts] = useState({});
  const [loadingDevMap, setLoadingDevMap] = useState({});

  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects().catch((err) => toast.error(err.message || 'Failed to load projects'));
    }
  }, [fetchProjects, projects.length]);

  useEffect(() => {
    if (!projects.length) return;
    setLoadingDevMap(
      Object.fromEntries(projects.map((p) => [p.id, true]))
    );
    Promise.allSettled(
      projects.map((p) =>
        devCapacityService.getProjectDevelopers(p.id).then((devs) => ({ id: p.id, count: devs.length }))
      )
    ).then((results) => {
      const counts = {};
      results.forEach((r, index) => {
        const projectId = projects[index].id;

        if (r.status === 'fulfilled') {
          counts[projectId] = r.value.count;
        } else {
          counts[projectId] = 'error'; // mark failure
        }
      });
      setDevCounts(counts);
    }).finally(() => {
      setLoadingDevMap({});
    });
  }, [projects]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Capacity Overview</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Developer capacity across all projects
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/projects')}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        >
          ← Back to Projects
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-slate-500 dark:text-slate-400">
          <ArrowPathIcon className="size-5 animate-spin" />
          <span className="text-sm">Loading projects...</span>
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-12 text-center">
          <p className="text-slate-600 dark:text-slate-400">No projects found.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => {
            const devCount = devCounts[project.id];
            const isError = devCount === 'error';
            const hasDevs = typeof devCount === 'number' && devCount > 0;

            return (
              <div
                key={project.id}
                className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col"
              >
                {/* Card Body */}
                <div className="p-5 flex-1">
                  <div className="min-w-0 mb-3">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white truncate" title={project.name}>
                      {project.name}
                    </h3>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                      {project.jira_project_key || project.key}
                    </span>
                  </div>

                  <div className="space-y-2 mt-3">
                    {/* Developer count */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">Developers assigned</span>
                      {loadingDevMap[project.id] ? (
                        <ArrowPathIcon className="size-3 animate-spin text-slate-400" />
                      ) : isError ? (
                        <button
                          onClick={() => {
                            setLoadingDevMap((prev) => ({
                              ...prev,
                              [project.id]: true,
                            }));
                            devCapacityService.getProjectDevelopers(project.id)
                              .then((devs) => {
                                setDevCounts((prev) => ({
                                  ...prev,
                                  [project.id]: devs.length,
                                }));
                              })
                              .catch(() => {
                                toast.error('Failed to load developers');
                              })
                              .finally(() =>
                                setLoadingDevMap((prev) => ({
                                  ...prev,
                                  [project.id]: false,
                                }))
                              );
                          }}
                          className="text-red-500 hover:text-red-600"
                          title="Retry"
                        >
                          <ArrowPathIcon className="size-4" />
                        </button>
                      ) : hasDevs ? (
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          {devCount} developer{devCount !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400 font-medium text-xs">
                          No developers
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-5 pb-5 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => navigate(`/projects/${project.id}/capacity`)}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                  >
                    <UserGroupIcon className="size-4" />
                    Manage Capacity
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}